import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import LeavePieChart from "./leavePieChart";

const LeaveDetail = ({
  year,
  annualLeaves,
  leaveTypesCount,
  totalAvailLeaves,
  remaingDays,
  empYearLeaves = [],
  publicHolidays = [],
  leaveTypes = [],
  leaveAllowanceSummary = null,
  noDeductibleLeaveTypesInfo = null,
  annualTrends = [],
  timeOffs = null,
  pieChartData = []
}) => {
  const { t } = useTranslation();
  // Process data for statistics using API data
  const statistics = useMemo(() => {
    // Use leaveAllowanceSummary if available, otherwise calculate from props
    const summary = leaveAllowanceSummary || {};
    const totalAllowedDays = summary.totalAllowedDays || 0;
    const totalAllowedHours = summary.totalAllowedHours || 0;
    const remainingDays = summary.totalRemainingDays ?? remaingDays ?? 0;
    const remainingHours = summary.totalRemainingHours || 0;
    const usedDays = summary.totalUsedDays || 0;
    const usedHours = summary.totalUsedHours || 0;

    // Get all leave types from leaveTypeBreakdown
    const leaveTypeBreakdown = summary.leaveTypeBreakdown || [];

    // Get public holidays from timeOffs
    const publicHolidayCount = timeOffs?.publicHolidays || publicHolidays?.length || 0;

    // Get dynamic time off types from timeOffs.timeOffTypes array
    const timeOffTypes = timeOffs?.timeOffTypes || [];

    // Use annualTrends from API if available, otherwise calculate from empYearLeaves
    let monthlyTrends = Array(12).fill(0);
    if (annualTrends && Array.isArray(annualTrends) && annualTrends.length > 0) {
      // API provides month as 1-12, we need 0-11 for array index
      annualTrends.forEach(trend => {
        const monthIndex = trend.month - 1; // Convert 1-12 to 0-11
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyTrends[monthIndex] = trend.days || 0;
        }
      });
    } else if (empYearLeaves && Array.isArray(empYearLeaves)) {
      // Fallback: calculate from empYearLeaves
      empYearLeaves.forEach(leave => {
        if (leave.startDate) {
          const date = moment(leave.startDate);
          const month = date.month(); // 0-11
          const days = leave.totalDays || 1;
          monthlyTrends[month] += days;
        }
      });
    }

    return {
      totalAllowedDays,
      totalAllowedHours,
      remainingDays,
      remainingHours,
      usedDays,
      usedHours,
      publicHolidayCount,
      timeOffTypes,
      monthlyTrends,
      leaveTypeBreakdown
    };
  }, [annualLeaves, leaveTypesCount, empYearLeaves, publicHolidays, leaveAllowanceSummary, annualTrends, timeOffs, remaingDays]);

  // Prepare data for pie chart - use pieChartData from API or calculate from leaveAllowanceSummary
  const chartData = useMemo(() => {
    // If API provides pieChartData, use it
    if (pieChartData && Array.isArray(pieChartData) && pieChartData.length > 0) {
      return pieChartData.map(item => ({
        name: item.leaveTypeName || 'Unknown',
        value: item.remainingDays || 0,
        color: item.color || '#667eea'
      }));
    }

    // Otherwise, use leaveAllowanceSummary.leaveTypeBreakdown
    if (leaveAllowanceSummary && leaveAllowanceSummary.leaveTypeBreakdown && Array.isArray(leaveAllowanceSummary.leaveTypeBreakdown)) {
      return leaveAllowanceSummary.leaveTypeBreakdown.map(item => ({
        name: item.leaveTypeName || 'Unknown',
        value: item.used || 0,
        color: item.color || '#667eea'
      }));
    }

    // Fallback: use leaveTypesCount
    if (!leaveTypesCount) {
      return [];
    }

    const data = [];
    if (typeof leaveTypesCount === 'object' && !Array.isArray(leaveTypesCount)) {
      Object.keys(leaveTypesCount).forEach(key => {
        const value = leaveTypesCount[key];
        const numValue = typeof value === 'number' ? value : (value?.count || value?.value || 0);
        if (numValue > 0 && key !== 'Total' && key !== 'total') {
          const leaveType = leaveTypes.find(lt => lt.name === key);
          data.push({
            name: key,
            value: numValue,
            color: leaveType?.color || '#667eea'
          });
        }
      });
    }

    return data;
  }, [pieChartData, leaveAllowanceSummary, leaveTypesCount, leaveTypes]);

  return (
    <div className="yearCalRightPanel">
      {/* Year End Card */}
      <div className="calRightInner leave-detail-card">
        <h1>
          {year}
          <span>{t("app.myCalendar.yearEndDecember")}</span>
        </h1>
        {/* Days / Hours, leave type breakdown and Total row: hide when no deductible leave types */}
        {noDeductibleLeaveTypesInfo?.noDeductibleLeaveTypes !== true && (
          <>
            <div className="calDetails">
              <span>{t("app.myCalendar.daysHours")}</span>
            </div>
            {/* Dynamic leave types from leaveTypeBreakdown */}
            {statistics.leaveTypeBreakdown && Array.isArray(statistics.leaveTypeBreakdown) && statistics.leaveTypeBreakdown.length > 0 && (
              statistics.leaveTypeBreakdown.map((leaveType, index) => {
                // Only show if totalAllowed > 0
                if (leaveType.totalAllowed > 0) {
                  const division = leaveType.leaveDivision || "Days";
                  const unit = division === "Hours" ? t("app.myCalendar.hoursUnit") : t("app.myCalendar.daysUnit");

                  return (
                    <div key={leaveType.leaveTypeId || index} className="calDetails">
                      <span className="calleftLabel">{leaveType.leaveTypeName || 'Unknown'}</span>
                      <span className="calrightLabel">
                        {leaveType.totalAllowed} {unit}
                      </span>
                    </div>
                  );
                }
                return null;
              })
            )}
            <div className="calDetails calTotal">
              <span className="calleftLabel">{t("app.myCalendar.total")}</span>
              <span className="calrightLabel">
                {(() => {
                  const parts = [];
                  if (statistics.totalAllowedDays > 0 || (statistics.totalAllowedDays === 0 && statistics.totalAllowedHours === 0)) {
                    parts.push(`${statistics.totalAllowedDays} ${t("app.myCalendar.daysUnit")}`);
                  }
                  if (statistics.totalAllowedHours > 0) {
                    parts.push(`${statistics.totalAllowedHours} ${t("app.myCalendar.hoursUnit")}`);
                  }
                  return parts.length > 0 ? parts.join(' / ') : t("app.myCalendar.zeroDays");
                })()}
              </span>
            </div>
          </>
        )}
        {/* When no deductible leave types: show Total leaves utilized; otherwise show Days remaining */}
        {noDeductibleLeaveTypesInfo?.noDeductibleLeaveTypes === true ? (
          <div className="calDetails calTotal days-remaining-bar">
            <span className="calleftLabel">{t("app.myCalendar.totalLeavesUtilized")}</span>
            <span className="calrightLabel">
              <i className="remainingNum">{noDeductibleLeaveTypesInfo?.totalLeavesUtilized ?? 0}</i>
            </span>
          </div>
        ) : (
          <div className="calDetails calTotal days-remaining-bar">
            <span className="calleftLabel">{t("app.myCalendar.daysRemaining")}</span>
            <span className="calrightLabel">
              <i className="remainingNum">{statistics.remainingDays || 0}</i>
            </span>
          </div>
        )}
      </div>

      {/* Your Time Off Card */}
      <div className="calRightInner leave-detail-card">
        <h2 className="card-title">{t("app.myCalendar.yourTimeOff")}</h2>
        {/* Public holidays - always show if count > 0 */}
        {statistics.publicHolidayCount > 0 && (
          <div className="calDetails">
            <span className="calleftLabel">{t("app.myCalendar.publicHolidays")}</span>
            <span className="calrightLabel">
              {statistics.publicHolidayCount}
            </span>
          </div>
        )}
        {/* Dynamic time off types from API */}
        {statistics.timeOffTypes && Array.isArray(statistics.timeOffTypes) && statistics.timeOffTypes.length > 0 && (
          statistics.timeOffTypes.map((timeOff, index) => {
            // Handle different possible structures from API
            const name = timeOff.name || timeOff.leaveTypeName || timeOff.typeName || 'Unknown';
            const count = timeOff.count || timeOff.days || timeOff.value || 0;

            // Only show if count > 0
            if (count > 0) {
              return (
                <div key={index} className="calDetails">
                  <span className="calleftLabel">{name}</span>
                  <span className="calrightLabel">
                    {count}
                  </span>
                </div>
              );
            }
            return null;
          })
        )}
      </div>

      {/* Annual Trends Card */}
      <div className="calRightInner leave-detail-card">
        <h2 className="card-title">{t("app.myCalendar.leavesTrends")}</h2>
        <div className="annual-trends-chart">
          <div className="trends-bars">
            {statistics.monthlyTrends.map((value, index) => {
              const maxValue = Math.max(...statistics.monthlyTrends, 1);
              const height = maxValue > 0 ? (value / maxValue) * 100 : 0;
              const monthNames = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
              const isHighValue = value > maxValue * 0.5;

              return (
                <div key={index} className="trend-bar-container">
                  <div
                    className={`trend-bar ${isHighValue ? 'trend-bar-high' : 'trend-bar-low'}`}
                    style={{ height: `${height}%` }}
                  >
                    {value > 0 && <span className="trend-value">{value}</span>}
                  </div>
                  <span className="trend-month">{monthNames[index]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pie Chart Card - Always show */}
      <div className="calRightInner leave-detail-card pie-chart-card">
        <LeavePieChart
          data={chartData}
          totalDays={statistics.remainingDays}
          usedDays={statistics.usedDays}
          year={year}
          annualLeaves={statistics.totalAllowedDays}
        />
      </div>
    </div>
  );
};

export default LeaveDetail;
